/**
 * Platform Detector
 *
 * Detects the user's platform (OS) from the User-Agent string.
 * Fonts are tied to the OS, not the browser — TikTok/FB/IG WebView
 * on iOS has the same fonts as Safari on iOS.
 *
 * @module font-config/services
 */

import type { PlatformId } from '../types/fontConfig.types';

/**
 * Ordered User-Agent rules — the first match wins. Order matters: iOS before macOS/Linux (an
 * iPad UA can contain "Linux"), Android before Linux. Shared with the published page's <head>
 * script (utils/platformFonts.ts → platformHeadScript), so the static page and the client
 * detect the same platform.
 */
export const PLATFORM_UA_RULES: ReadonlyArray<readonly [pattern: string, platform: PlatformId]> = [
  ['iphone|ipad|ipod', 'ios'], // includes TikTok/FB/IG WebView on iOS
  ['macintosh', 'macos'], // desktop Safari/Chrome
  ['android', 'android'], // includes TikTok/FB/IG WebView on Android
  ['windows', 'windows'],
  ['linux', 'linux'], // desktop — after Android
];

/** Null, empty or unknown UA: the most common non-Apple mobile platform. */
export const DEFAULT_PLATFORM: PlatformId = 'android';

/**
 * Detect the platform from a User-Agent string.
 *
 * @param userAgent - User-Agent string (from SERVER_DATA.userAgent or navigator.userAgent)
 * @returns Detected platform ID. Defaults to DEFAULT_PLATFORM.
 */
export function detectPlatform(userAgent: string | null | undefined): PlatformId {
  if (!userAgent) return DEFAULT_PLATFORM;
  const ua = userAgent.toLowerCase();
  for (const [pattern, platform] of PLATFORM_UA_RULES) {
    if (new RegExp(pattern).test(ua)) return platform;
  }
  return DEFAULT_PLATFORM;
}
